import React, { useState } from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { InlineError } from '@piwikpro/platform/components/Errors';
import {
  Stack, Badge, Button, withTranslation, Skeleton, Text, formatDate,
} from '@piwikpro/ui-components';
import { MarketingCratePaymentsState } from '../../redux';
import ManagePaymentModal from '../ManagePaymentModal/ManagePaymentModal';

const mapStateToProps = (state: MarketingCratePaymentsState) => {
  const { details, isLoading: detailLoading } = state.payments.subscription;
  const { isLoading: tiersLoading, failed } = state.payments.tiers;

  return {
    details,
    isLoading: detailLoading || tiersLoading,
    failed,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;


interface Props extends ReduxProps {
  t: TFunction
  hideManagePaymentButton?: Boolean
  hidePlanName?: boolean
}

export const PaymentBadge: React.FC<Props> = ({
  details,
  isLoading,
  t,
  hideManagePaymentButton,
  hidePlanName,
  failed,
}) => {
  const [paymentManageModalOpened, setPaymentManageModalOpened] = useState(false);

  if (failed) {
    return <InlineError />;
  }
  if (isLoading || !details) {
    return <Skeleton width="256px" height="24px" roundBorder />;
  }

  const getBadgeLabel = () => {
    if (details.scheduled_change_action === 'cancel') return 'paddle.payment.current-subscription.badge.cancelled';
    return details.billing_cycle === 'month' ? 'paddle.payment.current-subscription.billing-cycle.monthly' : 'paddle.payment.current-subscription.billing-cycle.yearly';
  };

  // Any scheduled change (cancel or pending downgrade) uses inactive icon + "Active until".
  const hasScheduledChange = Boolean(details.scheduled_change_action);
  const captionDate = hasScheduledChange
    ? details.scheduled_change_effective_at
    : details.next_payment;
  const showActiveUntil = hasScheduledChange || !details.next_payment;

  const formattedCaptionDate = captionDate
    ? `${formatDate(new Date(captionDate), { format: 'LL', timeZone: 'UTC' })} (UTC)`
    : null;

  return (
    <>
      <Stack verticalAlignment="center">
        {!hidePlanName && (
          <Stack.Item>
            <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
              {t('paddle.payment.current-subscription.business.text')}
            </Text>
          </Stack.Item>
        )
        }
        <Stack.Item previousSpacing={hidePlanName ? 'default' : 'narrow'}>
          <Badge
            size="medium"
            color="white"
            text={t(getBadgeLabel())}
            iconLeft={{
              name: hasScheduledChange ? 'refresh-error' : 'refresh',
              color: hasScheduledChange ? 'gray' : 'green',
              size: 'small',
            }}
          />
        </Stack.Item>
        <Stack.Item previousSpacing="narrow" fill>
          <Text as="p" size={12} color="InkLighter" dataId="Caption">
            {t(showActiveUntil
              ? 'paddle.payment.current-subscription.ends'
              : 'paddle.payment.current-subscription.next-payment')}
            {' '}
            {!showActiveUntil && details.status === 'past_due' ? (
              <Text size={12} as="span" color="Red">{formattedCaptionDate}</Text>
            ) : (
              formattedCaptionDate
            )}
          </Text>
        </Stack.Item>
        {
          !hideManagePaymentButton && (
            <Stack.Item>
              <Button
                icon="external-link"
                text={t('paddle.payment.current-subscription.manage-payment.btn-text')}
                onClick={() => { setPaymentManageModalOpened(true); }}
              />
            </Stack.Item>
          )
        }
      </Stack>
      {paymentManageModalOpened && (
        <ManagePaymentModal onClose={() => { setPaymentManageModalOpened(false); }} />
      )}
    </>
  );
};

export default connector(withTranslation(['administration'])((PaymentBadge)));
